import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog'
import { CatPeeking } from '@/components/mascot'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import {
  apiV2,
  ApiV2Error,
  type V2EscalationPolicy,
  type V2AlertChannel,
  type V2OnCallSchedule,
} from '@/lib/api-v2-client'

/** Read-side step shape incl. on_call_schedule_id (the generated api-v2.ts type
 *  lags until regenerated; cast to read it). */
type StepRead = { alert_channel_id?: string; on_call_schedule_id?: string; delay_minutes?: number; id?: string }
import { toast } from 'sonner'
import { ProOnlyPage } from '@/components/pro-only-page'

export const Route = createFileRoute('/_authenticated/dashboard/alerts/escalation')({
  component: () => (
    <ProOnlyPage
      title="Escalation policies are on Pro"
      text="Escalate unacknowledged alerts to more people after a delay. Escalation policies need a paid plan."
    >
      <EscalationPoliciesPage />
    </ProOnlyPage>
  ),
})

function EscalationPoliciesPage() {
  const queryClient = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [editPolicy, setEditPolicy] = useState<V2EscalationPolicy | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<V2EscalationPolicy | null>(null)

  const { data: policiesResponse, isLoading } = useQuery({
    queryKey: ['escalation-policies'],
    queryFn: () => apiV2.listEscalationPolicies(),
  })

  const { data: channelsResponse } = useQuery({
    queryKey: ['alert-channels'],
    queryFn: () => apiV2.listAlertChannels(),
  })

  const { data: schedulesResponse } = useQuery({
    queryKey: ['on-call-schedules'],
    queryFn: () => apiV2.listOnCallSchedules(),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiV2.deleteEscalationPolicy(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['escalation-policies'] })
      setDeleteTarget(null)
      toast.success('Policy deleted')
    },
    onError: (err) =>
      toast.error(err instanceof ApiV2Error ? err.message : 'Failed to delete policy'),
  })

  const policies = policiesResponse?.data ?? []
  const channels = channelsResponse?.data ?? []
  const schedules = schedulesResponse?.data ?? []

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold">Escalation Policies</h1>
          <p className="text-muted-foreground">
            Define multi-step alert escalation when incidents aren't acknowledged
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button>Create Policy</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <PolicyForm
              channels={channels}
              schedules={schedules}
              onSuccess={() => {
                setCreateOpen(false)
                queryClient.invalidateQueries({ queryKey: ['escalation-policies'] })
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Policies</CardTitle>
          <CardDescription>
            Assign a policy to a monitor in its edit page
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-12 animate-pulse rounded bg-muted" />
              ))}
            </div>
          ) : policies.length === 0 ? (
            <div className="py-8 text-center">
              <CatPeeking size={100} className="mx-auto mb-3" />
              <p className="text-muted-foreground">
                No escalation policies yet. Create one to get started.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Steps</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {policies.map((policy) => (
                  <TableRow key={policy.id}>
                    <TableCell className="font-medium">{policy.name}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {policy.steps.length === 0 ? (
                          <span className="text-sm text-muted-foreground">No steps</span>
                        ) : (
                          policy.steps.map((step, i) => {
                            const r = step as StepRead
                            const label = r.on_call_schedule_id
                              ? `📟 ${schedules.find((s) => s.id === r.on_call_schedule_id)?.name ?? 'On-call'}`
                              : (channels.find((c) => c.id === r.alert_channel_id)?.name ?? 'Unknown')
                            return (
                              <Badge key={r.id || i} variant="secondary" className="text-xs">
                                {i + 1}. {label}
                                {(r.delay_minutes ?? 0) > 0 && ` (+${r.delay_minutes}m)`}
                              </Badge>
                            )
                          })
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {policy.description || '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditPolicy(policy)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-status-down hover:text-status-down"
                          onClick={() => setDeleteTarget(policy)}
                        >
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editPolicy} onOpenChange={(open) => !open && setEditPolicy(null)}>
        <DialogContent className="max-w-lg">
          {editPolicy && (
            <PolicyForm
              policy={editPolicy}
              channels={channels}
              schedules={schedules}
              onSuccess={() => {
                setEditPolicy(null)
                queryClient.invalidateQueries({ queryKey: ['escalation-policies'] })
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Policy</DialogTitle>
            <DialogDescription>
              Delete "{deleteTarget?.name}"? Monitors using this policy will fall back to alerting all channels simultaneously.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ─────────────────────────────────────────────────
// Policy Form (create + edit)
// ─────────────────────────────────────────────────

type StepDraft = {
  target_type: 'channel' | 'schedule'
  alert_channel_id: string
  on_call_schedule_id: string
  delay_minutes: number
}

function PolicyForm({
  policy,
  channels,
  schedules,
  onSuccess,
}: {
  policy?: V2EscalationPolicy
  channels: V2AlertChannel[]
  schedules: V2OnCallSchedule[]
  onSuccess: () => void
}) {
  const [name, setName] = useState(policy?.name ?? '')
  const [description, setDescription] = useState(policy?.description ?? '')
  const [steps, setSteps] = useState<StepDraft[]>(
    policy?.steps.map((s) => {
      const r = s as StepRead
      return {
        target_type: r.on_call_schedule_id ? 'schedule' : 'channel',
        alert_channel_id: r.alert_channel_id ?? '',
        on_call_schedule_id: r.on_call_schedule_id ?? '',
        delay_minutes: r.delay_minutes ?? 0,
      }
    }) ?? []
  )

  const mutation = useMutation({
    mutationFn: () => {
      const stepsPayload = steps.map((s, i) => ({
        alert_channel_id: s.target_type === 'channel' ? s.alert_channel_id : undefined,
        on_call_schedule_id: s.target_type === 'schedule' ? s.on_call_schedule_id : undefined,
        step_order: i + 1,
        delay_minutes: s.delay_minutes,
      }))

      if (policy) {
        return apiV2.updateEscalationPolicy(policy.id, {
          name,
          description: description || undefined,
          steps: stepsPayload,
        })
      }
      return apiV2.createEscalationPolicy({
        name,
        description: description || undefined,
        steps: stepsPayload,
      })
    },
    onSuccess: () => {
      toast.success(policy ? 'Policy updated' : 'Policy created')
      onSuccess()
    },
    onError: (err) =>
      toast.error(err instanceof ApiV2Error ? err.message : 'Failed to save policy'),
  })

  function addStep() {
    const def: StepDraft =
      channels.length > 0
        ? { target_type: 'channel', alert_channel_id: channels[0].id, on_call_schedule_id: '', delay_minutes: 5 }
        : schedules.length > 0
          ? { target_type: 'schedule', alert_channel_id: '', on_call_schedule_id: schedules[0].id, delay_minutes: 5 }
          : { target_type: 'channel', alert_channel_id: '', on_call_schedule_id: '', delay_minutes: 5 }
    setSteps((prev) => [...prev, def])
  }

  function removeStep(index: number) {
    setSteps((prev) => prev.filter((_, i) => i !== index))
  }

  function updateStep(index: number, field: keyof StepDraft, value: string | number) {
    setSteps((prev) =>
      prev.map((s, i) => (i === index ? { ...s, [field]: value } : s))
    )
  }

  function moveStep(index: number, direction: -1 | 1) {
    const newIndex = index + direction
    if (newIndex < 0 || newIndex >= steps.length) return
    setSteps((prev) => {
      const copy = [...prev]
      ;[copy[index], copy[newIndex]] = [copy[newIndex], copy[index]]
      return copy
    })
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>{policy ? 'Edit Policy' : 'Create Escalation Policy'}</DialogTitle>
        <DialogDescription>
          Steps run in order. Each step waits its delay before alerting.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-2">
        <div className="space-y-2">
          <Label htmlFor="policy-name">Name</Label>
          <Input
            id="policy-name"
            placeholder="e.g., Critical Alert Chain"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="policy-description">Description (optional)</Label>
          <Input
            id="policy-description"
            placeholder="When to use this policy"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Steps</Label>
            <Button type="button" variant="outline" size="sm" onClick={addStep}>
              Add Step
            </Button>
          </div>

          {steps.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">
              No steps yet. Add a step to define the escalation chain.
            </p>
          ) : (
            <div className="space-y-2">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 rounded border p-2"
                >
                  <span className="text-xs font-medium text-muted-foreground w-5">
                    {index + 1}.
                  </span>

                  <Select
                    value={step.target_type}
                    onValueChange={(v) => updateStep(index, 'target_type', v)}
                  >
                    <SelectTrigger className="w-24 h-8" aria-label={`Step ${index + 1} target type`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="channel">Channel</SelectItem>
                      <SelectItem value="schedule">On-call</SelectItem>
                    </SelectContent>
                  </Select>

                  {step.target_type === 'channel' ? (
                    <Select
                      value={step.alert_channel_id}
                      onValueChange={(v) => updateStep(index, 'alert_channel_id', v)}
                    >
                      <SelectTrigger className="flex-1 h-8" aria-label={`Step ${index + 1} alert channel`}>
                        <SelectValue placeholder="Select channel" />
                      </SelectTrigger>
                      <SelectContent>
                        {channels.map((ch) => (
                          <SelectItem key={ch.id} value={ch.id}>
                            {ch.name} ({ch.type})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Select
                      value={step.on_call_schedule_id}
                      onValueChange={(v) => updateStep(index, 'on_call_schedule_id', v)}
                    >
                      <SelectTrigger className="flex-1 h-8" aria-label={`Step ${index + 1} on-call schedule`}>
                        <SelectValue placeholder="Select schedule" />
                      </SelectTrigger>
                      <SelectContent>
                        {schedules.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  <div className="flex items-center gap-1">
                    <span className="text-xs text-muted-foreground">+</span>
                    <Input
                      type="number"
                      aria-label={`Step ${index + 1} delay in minutes`}
                      min="0"
                      className="w-16 h-8 text-center"
                      value={step.delay_minutes}
                      onChange={(e) =>
                        updateStep(index, 'delay_minutes', parseInt(e.target.value) || 0)
                      }
                    />
                    <span className="text-xs text-muted-foreground">min</span>
                  </div>

                  <div className="flex gap-0.5">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      disabled={index === 0}
                      onClick={() => moveStep(index, -1)}
                    >
                      ↑
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      disabled={index === steps.length - 1}
                      onClick={() => moveStep(index, 1)}
                    >
                      ↓
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-status-down hover:text-status-down"
                      onClick={() => removeStep(index)}
                    >
                      ×
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <DialogFooter>
        <Button
          onClick={() => mutation.mutate()}
          disabled={!name || mutation.isPending}
        >
          {mutation.isPending ? 'Saving...' : policy ? 'Save Changes' : 'Create Policy'}
        </Button>
      </DialogFooter>
    </>
  )
}
